(() => {
  if (window.__awSignatureProductsLoaded) {
    return;
  }

  window.__awSignatureProductsLoaded = true;

  const instances = new Map();
  const selectors = {
    root: "[data-aw-signature-products]",
    tab: "[data-aw-signature-tab]",
    panel: "[data-aw-signature-panel]",
  };

  class AWSignatureProducts {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.tabs = Array.from(root.querySelectorAll(selectors.tab));
      this.panels = Array.from(root.querySelectorAll(selectors.panel));
      this.activeIndex = this.getInitialIndex();

      this.onTabClick = this.handleTabClick.bind(this);
      this.onTabKeydown = this.handleTabKeydown.bind(this);

      this.bindEvents();
      this.activate(this.activeIndex, false);
    }

    getInitialIndex() {
      const configuredIndex = Number.parseInt(this.root.dataset.initialTabIndex || "1", 10) - 1;

      if (Number.isInteger(configuredIndex) && configuredIndex >= 0 && configuredIndex < this.tabs.length) {
        return configuredIndex;
      }

      return 0;
    }

    bindEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.onTabClick);
        tab.addEventListener("keydown", this.onTabKeydown);
      });
    }

    handleTabClick(event) {
      const index = this.tabs.indexOf(event.currentTarget);

      if (index >= 0) {
        this.activate(index);
      }
    }

    handleTabKeydown(event) {
      const index = this.tabs.indexOf(event.currentTarget);

      if (index < 0) {
        return;
      }

      let nextIndex = index;

      switch (event.key) {
        case "ArrowRight":
          nextIndex = (index + 1) % this.tabs.length;
          break;
        case "ArrowLeft":
          nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = this.tabs.length - 1;
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          this.activate(index);
          return;
        default:
          return;
      }

      event.preventDefault();
      this.activate(nextIndex);
      this.tabs[nextIndex].focus();
    }

    activate(index, focus = true) {
      if (!this.tabs.length || index < 0 || index >= this.tabs.length) {
        return;
      }

      this.activeIndex = index;

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;

        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;

        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
      });

      if (focus) {
        this.tabs[index].focus();
      }
    }

    selectBlock(blockId, fallbackTarget) {
      let tab = blockId ? this.root.querySelector(`${selectors.tab}[data-block-id="${blockId}"]`) : null;

      if (!tab && fallbackTarget && typeof fallbackTarget.closest === "function") {
        tab = fallbackTarget.closest(selectors.tab);
      }

      const index = tab ? this.tabs.indexOf(tab) : -1;

      if (index >= 0) {
        this.activate(index);
      }
    }

    destroy() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener("click", this.onTabClick);
        tab.removeEventListener("keydown", this.onTabKeydown);
      });
    }
  }

  function getRoot(target) {
    if (!target) {
      return null;
    }

    if (typeof target.matches === "function" && target.matches(selectors.root)) {
      return target;
    }

    if (typeof target.querySelector === "function") {
      const root = target.querySelector(selectors.root);

      if (root) {
        return root;
      }
    }

    if (typeof target.closest === "function") {
      return target.closest(selectors.root);
    }

    return null;
  }

  function destroySection(sectionId) {
    const instance = instances.get(sectionId);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initSection(root) {
    if (!root) {
      return;
    }

    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      return;
    }

    destroySection(sectionId);
    instances.set(sectionId, new AWSignatureProducts(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      initSection(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSection(getRoot(event.target));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = getRoot(event.target);

    if (!root) {
      return;
    }

    destroySection(root.dataset.sectionId);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const blockId = event.detail && event.detail.blockId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.selectBlock(blockId, event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
